import * as THREE from 'three';
import { CONFIG, PickupKind } from './config.js';
import { makeMaterials } from './materials.js';
import { Player } from './player.js';
import { World } from './world.js';
import { Powers } from './powers.js';
import { Input } from './input.js';
import { AudioStubs } from '../audio/stubs.js';

export class Game {
  constructor(canvas, ui) {
    this.canvas = canvas;
    this.ui = ui;
    this.state = 'title'; // title | playing | paused | dead
    this.score = 0;
    this.coins = 0;
    this.clock = new THREE.Clock(false);
    this.audio = new AudioStubs();

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x070b14);
    this.scene.fog = new THREE.Fog(0x070b14, 40, 160);

    this.camera = new THREE.PerspectiveCamera(55, 1, 0.1, 300);
    this.camera.position.set(0, 5.5, -8);

    // lights
    const hemi = new THREE.HemisphereLight(0x9ecbff, 0x0b1220, 0.85);
    this.scene.add(hemi);
    const sun = new THREE.DirectionalLight(0xffffff, 1.15);
    sun.position.set(-6, 18, 4);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 60;
    sun.shadow.camera.left = -20;
    sun.shadow.camera.right = 20;
    sun.shadow.camera.top = 20;
    sun.shadow.camera.bottom = -20;
    this.scene.add(sun);
    this.sun = sun;

    // subtle bend feel via camera roll / offset (optional, cheap)
    this.bendPhase = 0;

    this.mats = makeMaterials();
    this.player = new Player(this.scene, this.mats);
    this.world = new World(this.scene, this.mats);
    this.powers = new Powers();
    this.input = new Input(canvas);

    this._onResize = () => this.resize();
    window.addEventListener('resize', this._onResize);
    this.resize();

    this.ui.bind(this);
    this.world.reset();
    this.player.reset();
    this.syncCamera(1);
    this.raf = requestAnimationFrame((t) => this.frame(t));
  }

  resize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  start() {
    this.audio.ensure();
    this.score = 0;
    this.coins = 0;
    this.powers.reset();
    this.world.reset();
    this.player.reset();
    this.state = 'playing';
    this.clock.start();
    this.clock.getDelta();
    this.ui.showPlaying();
    this.ui.update(this);
  }

  pause() {
    if (this.state !== 'playing') return;
    this.state = 'paused';
    this.ui.showPaused();
  }

  resume() {
    if (this.state !== 'paused') return;
    this.state = 'playing';
    this.clock.getDelta();
    this.ui.showPlaying();
  }

  togglePause() {
    if (this.state === 'playing') this.pause();
    else if (this.state === 'paused') this.resume();
  }

  die() {
    this.state = 'dead';
    this.audio.hit();
    this.player.alive = false;
    this.ui.showGameOver(this.score, this.coins);
  }

  frame() {
    this.raf = requestAnimationFrame(() => this.frame());
    const dt = Math.min(this.clock.getDelta(), 0.05);

    if (this.input.consumePause()) this.togglePause();

    if (this.state === 'playing') {
      this.tick(dt);
    }

    this.renderer.render(this.scene, this.camera);
  }

  tick(dt) {
    const actions = this.input.consume();
    for (const a of actions) {
      if (a === 'left' && this.player.requestLane(-1)) this.audio.lane();
      if (a === 'right' && this.player.requestLane(1)) this.audio.lane();
      if (a === 'jump' && this.player.requestJump()) this.audio.jump();
      if (a === 'roll' && this.player.requestRoll()) this.audio.roll();
    }

    this.player.update(dt, this.world);
    this.world.update(this.player.z);
    this.powers.update(dt);
    this.player.setHoverboard(this.powers.boardActive);
    if (this.powers.boardActive) this.player.invuln = Math.max(this.player.invuln, 0.05);

    // score from distance
    this.score = Math.floor(this.player.distance * 2);

    const { hit, gathered } = this.world.collide(this.player, this.powers.magnetActive);
    for (const p of gathered) {
      if (p.kind === PickupKind.COIN) {
        this.coins += CONFIG.coinValue;
        this.score += 10;
        this.audio.coin();
      } else if (p.kind === PickupKind.MAGNET) {
        this.powers.grantMagnet();
        this.audio.power();
      } else if (p.kind === PickupKind.HOVERBOARD) {
        this.powers.grantHoverboard();
        this.audio.power();
      }
    }

    if (hit && !this.player.getBounds().invuln) {
      if (this.powers.tryRevive()) {
        this.player.invuln = 1.25;
        this.audio.revive();
      } else {
        this.die();
      }
    }

    this.bendPhase += dt * 0.35;
    this.syncCamera(dt);
    this.ui.update(this);
  }

  syncCamera(dt) {
    const p = this.player;
    // slight world bend feel: horizontal sway + look-ahead
    const bend = Math.sin(this.bendPhase) * 0.35;
    const desired = new THREE.Vector3(
      p.x * 0.85 + bend,
      4.8 + (p.y > 0 ? 0.4 : 0),
      p.z - 9.5
    );
    this.camera.position.lerp(desired, 1 - Math.pow(0.001, dt));
    const look = new THREE.Vector3(p.x * 0.5 + bend * 0.5, 1.2 + p.y * 0.3, p.z + 14);
    this.camera.lookAt(look);
    this.sun.position.set(p.x - 6, 18, p.z + 4);
    this.sun.target.position.set(p.x, 0, p.z + 10);
    this.sun.target.updateMatrixWorld();
  }
}
